import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const WorkOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const token = localStorage.getItem('token');

  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [agents, setAgents] = useState([]);

  // Modals / inline forms
  const [showAssign, setShowAssign] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [showTimeLog, setShowTimeLog] = useState(false);
  const [timeLogData, setTimeLogData] = useState({ time_type: 'work', start_time: '', end_time: '', notes: '' });
  const [showPartForm, setShowPartForm] = useState(false);
  const [partData, setPartData] = useState({ part_name: '', part_name_ar: '', part_number: '', quantity: 1, unit_cost: 0 });
  const [erpSearch, setErpSearch] = useState('');
  const [erpResults, setErpResults] = useState([]);
  const [erpSearching, setErpSearching] = useState(false);
  const [showErpDropdown, setShowErpDropdown] = useState(false);
  const erpTimerRef = React.useRef(null);

  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({ wo_total: 0, total_invoiced: 0, remaining: 0, can_invoice: false });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [editDiscount, setEditDiscount] = useState(0);
  const [editTax, setEditTax] = useState(0);
  const [editDate, setEditDate] = useState('');
  const [fsmSettings, setFsmSettings] = useState({});
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ item_type: 'part', name: '', part_number: '', qty: 1, unit_price: 0, markup_percent: 0 });

  const searchErpProducts = (query) => {
    if (erpTimerRef.current) clearTimeout(erpTimerRef.current);
    if (!query || query.length < 2) { setErpResults([]); setShowErpDropdown(false); return; }
    erpTimerRef.current = setTimeout(async () => {
      setErpSearching(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/v1/fsm/erp-products?search=${encodeURIComponent(query)}&limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setErpResults(data.products || []);
        setShowErpDropdown(true);
      } catch (e) { console.error(e); }
      finally { setErpSearching(false); }
    }, 400);
  };

  const selectErpProduct = (product) => {
    setPartData(p => ({
      ...p,
      part_name: product.name || '',
      part_name_ar: product.name_ar || '',
      part_number: product.code || '',
      unit_cost: product.cost_price || product.selling_price || 0,
      erp_item_id: product.id
    }));
    setErpSearch(product.name + (product.code ? ' (' + product.code + ')' : ''));
    setShowErpDropdown(false);
  };
  const [statusNotes, setStatusNotes] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [targetStatus, setTargetStatus] = useState('');
  const [resolutionSummary, setResolutionSummary] = useState('');

  const fetchFsmSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/fsm/invoice-settings', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setFsmSettings(data.data || {});
    } catch (e) { console.error(e); }
  }, []);

  const formatDateDMY = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return dd + '/' + mm + '/' + yyyy;
  };

  const fetchInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/work-orders/${id}/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setInvoices(data.data.invoices || []);
        setInvoiceSummary(data.data.summary || {});
      }
    } catch (e) { console.error('Fetch invoices error:', e); }
    finally { setInvoiceLoading(false); }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'invoices' && id) { fetchInvoices(); fetchFsmSettings(); }
  }, [activeTab, id, fetchInvoices, fetchFsmSettings]);

  const handleGenerateInvoice = async () => {
    if (!window.confirm(isAr ? 'هل تريد إصدار فاتورة للبنود غير المفوترة؟' : 'Generate invoice for uninvoiced items?')) return;
    setInvoiceGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/work-orders/${id}/invoices/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        await fetchInvoices();
        await fetchInvoiceDetail(data.data.invoice_id);
        setEditingInvoice(true);
      } else {
        alert(data.message || 'Error');
      }
    } catch (e) {
      console.error('Generate invoice error:', e);
      alert(e.message);
    } finally { setInvoiceGenerating(false); }
  };

  const fetchInvoiceDetail = async (invoiceId) => {
    setInvoiceDetailLoading(true);
    setSelectedInvoice(invoiceId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/invoices/${invoiceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setInvoiceDetail(data.data);
        setEditNotes(data.data.notes || '');
        setEditItems((data.data.items || []).map(it => ({ ...it })));
        setEditDiscount(parseFloat(data.data.discount_percent) || 0);
        setEditTax(parseFloat(data.data.tax_percent) || parseFloat(fsmSettings.fsm_default_tax_percent) || 0);
        setEditDate(data.data.invoice_date ? data.data.invoice_date.slice(0, 10) : new Date().toISOString().slice(0, 10));
      }
    } catch (e) { console.error('Fetch invoice detail error:', e); }
    finally { setInvoiceDetailLoading(false); }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm(L.confirm_delete_inv)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert(L.invoice_deleted);
        setSelectedInvoice(null);
        setInvoiceDetail(null);
        fetchInvoices();
      } else { alert(data.message || 'Error'); }
    } catch (e) { alert(e.message); }
  };

  const handleSaveInvoiceEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/invoices/${selectedInvoice}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: editNotes, items: editItems, invoice_date: editDate, discount_percent: editDiscount, tax_percent: editTax })
      });
      const data = await res.json();
      if (data.success) {
        setEditingInvoice(false);
        setSelectedInvoice(null);
        setInvoiceDetail(null);
        fetchInvoices();
      } else { alert(data.message || 'Error'); }
    } catch (e) { alert(e.message); }
  };

  const updateEditItem = (idx, field, value) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      const q = parseFloat(updated[idx].qty) || 0;
      const p = parseFloat(updated[idx].unit_price) || 0;
      const m = parseFloat(updated[idx].markup_percent) || 0;
      updated[idx].total = Math.round(q * p * (1 + m / 100) * 100) / 100;
      return updated;
    });
  };

  const handleAddItem = async () => {
    if (!selectedInvoice || !newItem.name) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/invoices/${selectedInvoice}/items`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem)
      });
      const data = await res.json();
      if (data.success) {
        setShowAddItem(false);
        setNewItem({ item_type: 'part', name: '', part_number: '', qty: 1, unit_price: 0, markup_percent: 0 });
        fetchInvoiceDetail(selectedInvoice);
      } else { alert(data.message); }
    } catch (e) { alert(e.message); }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm(isAr ? 'حذف هذا البند؟' : 'Remove this item?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/v1/fsm/invoices/${selectedInvoice}/items/${itemId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchInvoiceDetail(selectedInvoice);
    } catch (e) { alert(e.message); }
  };

  const STATUS_TRANSITIONS = {
    draft: ['open', 'cancelled'], open: ['assigned', 'cancelled'],
    assigned: ['in_progress', 'open', 'cancelled'], in_progress: ['on_hold', 'completed', 'cancelled'],
    on_hold: ['in_progress', 'cancelled'], completed: ['closed'], closed: [], cancelled: []
  };

  const STATUS_COLORS = { draft: '#6b7280', open: '#3b82f6', assigned: '#8b5cf6', in_progress: '#f59e0b', on_hold: '#ef4444', completed: '#10b981', closed: '#6b7280', cancelled: '#991b1b' };
  const PRIORITY_COLORS = { critical: '#dc2626', high: '#f97316', medium: '#eab308', low: '#22c55e' };

  const L = {
    overview: isAr ? 'نظرة عامة' : 'Overview', checklist: isAr ? 'قائمة الفحص' : 'Checklist',
    time_parts: isAr ? 'الوقت والقطع' : 'Time & Parts', attachments: isAr ? 'المرفقات' : 'Attachments',
    history: isAr ? 'السجل' : 'History',
    invoices: isAr ? 'الفواتير' : 'Invoices',
    create_invoice: isAr ? 'إصدار فاتورة' : 'Create Invoice',
    invoice_created: isAr ? 'تم إصدار الفاتورة بنجاح' : 'Invoice created successfully',
    no_uninvoiced: isAr ? 'لا توجد بنود غير مفوترة' : 'No uninvoiced items',
    total_invoiced: isAr ? 'إجمالي المفوتر' : 'Total Invoiced',
    remaining_amount: isAr ? 'المتبقي' : 'Remaining',
    invoice_no: isAr ? 'فاتورة #' : 'Invoice #',
    parts_lbl: isAr ? 'قطع غيار' : 'Parts',
    labor_lbl: isAr ? 'عمالة' : 'Labor',
    delete_invoice: isAr ? 'حذف الفاتورة' : 'Delete Invoice',
    edit_invoice: isAr ? 'تعديل الفاتورة' : 'Edit Invoice',
    save_changes: isAr ? 'حفظ التعديلات' : 'Save Changes',
    confirm_delete_inv: isAr ? 'هل تريد حذف هذه الفاتورة؟ سيتم إرجاع الكميات المفوترة.' : 'Delete this invoice? Invoiced quantities will be reversed.',
    invoice_deleted: isAr ? 'تم حذف الفاتورة' : 'Invoice deleted',
    invoice_updated: isAr ? 'تم تعديل الفاتورة' : 'Invoice updated',
    qty_lbl: isAr ? 'الكمية' : 'Qty',
    price_lbl: isAr ? 'السعر' : 'Price',
    markup_lbl: isAr ? 'هامش ربح' : 'Markup %',
    total_lbl: isAr ? 'الإجمالي' : 'Total',
    close_lbl: isAr ? 'إغلاق' : 'Close',
    item_name: isAr ? 'البند' : 'Item',
    type_lbl: isAr ? 'النوع' : 'Type',
    notes_lbl: isAr ? 'ملاحظات' : 'Notes', edit: isAr ? 'تعديل' : 'Edit', assign: isAr ? 'تعيين' : 'Assign',
    start_work: isAr ? 'بدء العمل' : 'Start Work', complete: isAr ? 'إكمال' : 'Complete',
    cancel_wo: isAr ? 'إلغاء' : 'Cancel WO', back: isAr ? 'رجوع' : 'Back',
    equipment: isAr ? 'المعدات' : 'Equipment', client: isAr ? 'العميل' : 'Client',
    contract: isAr ? 'العقد' : 'Contract', location: isAr ? 'الموقع' : 'Location',
    scheduled: isAr ? 'الموعد' : 'Scheduled', assigned_to: isAr ? 'مسند إلى' : 'Assigned To',
    type: isAr ? 'النوع' : 'Type', source: isAr ? 'المصدر' : 'Source', priority: isAr ? 'الأولوية' : 'Priority',
    status: isAr ? 'الحالة' : 'Status', description: isAr ? 'الوصف' : 'Description',
    sla_response: isAr ? 'استجابة SLA' : 'SLA Response', sla_resolution: isAr ? 'حل SLA' : 'SLA Resolution',
    coverage: isAr ? 'التغطية' : 'Coverage', billable: isAr ? 'قابل للفوترة' : 'Billable',
    covered: isAr ? 'مغطى' : 'Covered', resolution: isAr ? 'ملخص الحل' : 'Resolution Summary',
    notes: isAr ? 'ملاحظات' : 'Notes', created: isAr ? 'تاريخ الإنشاء' : 'Created',
    save: isAr ? 'حفظ' : 'Save', cancel: isAr ? 'إلغاء' : 'Cancel', confirm: isAr ? 'تأكيد' : 'Confirm',
    add_time: isAr ? 'إضافة وقت' : 'Add Time Log', add_part: isAr ? 'إضافة قطعة' : 'Add Part',
    upload: isAr ? 'رفع ملف' : 'Upload File', no_items: isAr ? 'لا توجد عناصر' : 'No items',
    hours: isAr ? 'ساعة' : 'hrs', labor_cost: isAr ? 'تكلفة العمل' : 'Labor Cost',
    parts_cost: isAr ? 'تكلفة القطع' : 'Parts Cost', total_cost: isAr ? 'التكلفة الإجمالية' : 'Total Cost',
    change_status: isAr ? 'تغيير الحالة' : 'Change Status', reason: isAr ? 'السبب' : 'Reason',
    select_tech: isAr ? 'اختر الفني' : 'Select Technician', loading: isAr ? 'جاري التحميل...' : 'Loading...',
    time_type: isAr ? 'النوع' : 'Type', start: isAr ? 'البداية' : 'Start', end: isAr ? 'النهاية' : 'End',
    part_name: isAr ? 'اسم القطعة' : 'Part Name', quantity: isAr ? 'الكمية' : 'Qty',
    unit_cost: isAr ? 'سعر الوحدة' : 'Unit Cost', delete: isAr ? 'حذف' : 'Delete',
    resolution_prompt: isAr ? 'ملخص الحل (مطلوب)' : 'Resolution summary (required)',
    actual_start: isAr ? 'بداية فعلية' : 'Actual Start', actual_end: isAr ? 'نهاية فعلية' : 'Actual End',
    created_by: isAr ? 'أنشأه' : 'Created By', completed_by: isAr ? 'أكمله' : 'Completed By',
    rating: isAr ? 'التقييم' : 'Rating',
  };

  // Styles
  const cardSt = { backgroundColor: 'rgba(26, 32, 56, 0.8)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', padding: '20px' };
  const badgeSt = (color) => ({ display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: `${color}22`, color });
  const btnPrimary = { padding: '8px 18px', backgroundColor: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };
  const btnOutline = { padding: '8px 18px', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' };
  const btnDanger = { ...btnPrimary, backgroundColor: '#ef4444' };
  const btnSuccess = { ...btnPrimary, backgroundColor: '#10b981' };
  const inputSt = { width: '100%', padding: '10px', border: '2px solid rgba(255,255,255,0.2)', borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
  const labelSt = { display: 'block', marginBottom: '6px', color: '#9ca3af', fontSize: '12px', fontWeight: '500' };
  const infoRowSt = { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' };
  const infoLabelSt = { color: '#9ca3af', fontSize: '13px' };
  const infoValueSt = { color: '#f9fafb', fontSize: '13px', fontWeight: '500' };
  const tabSt = (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', border: 'none', borderRadius: '8px 8px 0 0',
    backgroundColor: active ? 'rgba(26, 32, 56, 0.8)' : 'transparent', color: active ? '#667eea' : '#9ca3af',
    borderBottom: active ? '2px solid #667eea' : '2px solid transparent',
  });
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
  const modalBox = { ...cardSt, width: '90%', maxWidth: '480px', padding: '24px' };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  // Fetch
  const fetchWO = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/v1/fsm/work-orders/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setWo(data.data);
    } catch (err) { console.error('Fetch WO error:', err); }
    finally { setLoading(false); }
  }, [id, token]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/agents', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setAgents(data.data || data.agents || []);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchWO(); fetchAgents(); }, [fetchWO, fetchAgents]);

  // Actions
  const doAction = async (url, method, body) => {
    try {
      const res = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await res.json();
      if (data.success) { fetchWO(); return true; }
      else { alert(data.message || 'Error'); return false; }
    } catch (err) { alert('Error: ' + err.message); return false; }
  };

  const handleStatusChange = async () => {
    if (targetStatus === 'completed') {
      if (!resolutionSummary.trim()) { alert(L.resolution_prompt); return; }
      await doAction(`/api/v1/fsm/work-orders/${id}/complete`, 'POST', { resolution_summary: resolutionSummary, notes: statusNotes });
    } else {
      await doAction(`/api/v1/fsm/work-orders/${id}/status`, 'PUT', { status: targetStatus, notes: statusNotes });
    }
    setShowStatusModal(false); setStatusNotes(''); setResolutionSummary(''); setTargetStatus('');
  };

  const handleAssign = async () => {
    if (!assignTo) return;
    await doAction(`/api/v1/fsm/work-orders/${id}/assign`, 'PUT', { assigned_to: parseInt(assignTo) });
    setShowAssign(false); setAssignTo('');
  };

  const handleChecklistToggle = async (index) => {
    const checklist = [...(typeof wo.checklist === 'string' ? JSON.parse(wo.checklist || '[]') : (wo.checklist || []))];
    checklist[index] = { ...checklist[index], checked: !checklist[index].checked, checked_at: !checklist[index].checked ? new Date().toISOString() : null };
    await doAction(`/api/v1/fsm/work-orders/${id}/checklist`, 'PUT', { checklist });
  };

  const handleAddTimeLog = async () => {
    if (!timeLogData.start_time) return;
    await doAction(`/api/v1/fsm/work-orders/${id}/time-logs`, 'POST', timeLogData);
    setShowTimeLog(false); setTimeLogData({ time_type: 'work', start_time: '', end_time: '', notes: '' });
  };

  const handleDeleteTimeLog = async (tlId) => { await doAction(`/api/v1/fsm/work-orders/${id}/time-logs/${tlId}`, 'DELETE'); };

  const handleAddPart = async () => {
    if (!partData.part_name) return;
    await doAction(`/api/v1/fsm/work-orders/${id}/parts`, 'POST', partData);
    setShowPartForm(false); setPartData({ part_name: '', part_name_ar: '', part_number: '', quantity: 1, unit_cost: 0, erp_item_id: null }); setErpSearch(''); setErpResults([]); setShowErpDropdown(false);
  };

  const handleDeletePart = async (pId) => { await doAction(`/api/v1/fsm/work-orders/${id}/parts/${pId}`, 'DELETE'); };

  const handleUploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachment_type', 'other');
    try {
      const res = await fetch(`/api/v1/fsm/work-orders/${id}/attachments`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      const data = await res.json();
      if (data.success) fetchWO(); else alert(data.message);
    } catch (err) { alert('Upload error'); }
  };

  const handleDeleteAttachment = async (attId) => { await doAction(`/api/v1/fsm/work-orders/${id}/attachments/${attId}`, 'DELETE'); };

  const openStatusModal = (status) => { setTargetStatus(status); setShowStatusModal(true); };

  if (loading || !wo) return <Layout><div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>{L.loading}</div></Layout>;

  const allowedTransitions = STATUS_TRANSITIONS[wo.status] || [];
  const checklist = wo.checklist ? (typeof wo.checklist === 'string' ? JSON.parse(wo.checklist) : wo.checklist) : [];
  const checklistProgress = checklist.length > 0 ? Math.round((checklist.filter(c => c.checked).length / checklist.length) * 100) : 0;

  
  // --- PDF Download ---
  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/fsm/work-orders/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${wo?.wo_number || 'work-order'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      alert(lang === 'ar' ? 'فشل تحميل PDF' : 'PDF download failed');
    }
  };

  return (
    <Layout>
    <div style={{ padding: '24px', direction: isAr ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <button style={{ ...btnOutline, marginBottom: '8px', padding: '6px 12px', fontSize: '12px' }} onClick={() => navigate('/fsm/work-orders')}>← {L.back}</button>
          <h1 style={{ color: '#f9fafb', fontSize: '22px', fontWeight: '700', margin: '4px 0' }}>🔧 {wo.wo_number}</h1>
          <p style={{ color: '#d1d5db', fontSize: '15px', margin: 0 }}>{isAr && wo.title_ar ? wo.title_ar : wo.title}</p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span style={badgeSt(STATUS_COLORS[wo.status] || '#6b7280')}>{wo.status}</span>
            <span style={badgeSt(PRIORITY_COLORS[wo.priority] || '#6b7280')}>{wo.priority}</span>
            <span style={badgeSt('#6b7280')}>{wo.wo_type}</span>
            {wo.is_billable ? <span style={badgeSt('#ef4444')}>💰 {L.billable}</span> : <span style={badgeSt('#10b981')}>✅ {L.covered}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!['closed', 'cancelled', 'completed'].includes(wo.status) && (
            <button style={btnPrimary} onClick={() => navigate(`/fsm/work-orders/${id}/edit`)}>✏️ {L.edit}</button>
          )}
          <button onClick={handleDownloadPDF} style={{
            padding: '8px 16px', borderRadius: '8px',
            border: '2px solid rgba(255,255,255,0.2)',
            backgroundColor: 'transparent', color: '#d1d5db',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            📄 {L?.downloadPdf || (lang === 'ar' ? 'تحميل PDF' : 'Download PDF')}
          </button>
          {['draft', 'open'].includes(wo.status) && <button style={{ ...btnPrimary, backgroundColor: '#8b5cf6' }} onClick={() => setShowAssign(true)}>👤 {L.assign}</button>}
          {wo.status === 'assigned' && <button style={btnSuccess} onClick={() => openStatusModal('in_progress')}>▶ {L.start_work}</button>}
          {wo.status === 'in_progress' && <button style={btnSuccess} onClick={() => openStatusModal('completed')}>✅ {L.complete}</button>}
          {allowedTransitions.includes('cancelled') && <button style={btnDanger} onClick={() => openStatusModal('cancelled')}>✕ {L.cancel_wo}</button>}
        </div>
      </div>

      {/* SLA Bar */}
      {wo.sla_info && (wo.sla_info.responsePercent !== null || wo.sla_info.resolutionPercent !== null) && (
        <div style={{ ...cardSt, marginBottom: '16px', padding: '14px 20px', display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
          {wo.sla_info.responsePercent !== null && (
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#9ca3af', fontSize: '12px' }}>{L.sla_response}</span>
                <span style={{ color: wo.sla_info.responsePercent >= 100 ? '#ef4444' : wo.sla_info.responsePercent >= 75 ? '#f59e0b' : '#10b981', fontSize: '12px', fontWeight: '600' }}>
                  {wo.sla_info.responsePercent}%
                </span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, wo.sla_info.responsePercent)}%`, height: '100%', borderRadius: '3px', backgroundColor: wo.sla_info.responsePercent >= 100 ? '#ef4444' : wo.sla_info.responsePercent >= 75 ? '#f59e0b' : '#10b981' }} />
              </div>
            </div>
          )}
          {wo.sla_info.resolutionPercent !== null && (
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#9ca3af', fontSize: '12px' }}>{L.sla_resolution}</span>
                <span style={{ color: wo.sla_info.resolutionPercent >= 100 ? '#ef4444' : wo.sla_info.resolutionPercent >= 75 ? '#f59e0b' : '#10b981', fontSize: '12px', fontWeight: '600' }}>
                  {wo.sla_info.resolutionPercent}%
                </span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, wo.sla_info.resolutionPercent)}%`, height: '100%', borderRadius: '3px', backgroundColor: wo.sla_info.resolutionPercent >= 100 ? '#ef4444' : wo.sla_info.resolutionPercent >= 75 ? '#f59e0b' : '#10b981' }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0', borderBottom: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap' }}>
        {['overview', 'checklist', 'time_parts', 'invoices', 'attachments', 'history'].map(tab => (
          <button key={tab} style={tabSt(activeTab === tab)} onClick={() => setActiveTab(tab)}>
            {L[tab]} {tab === 'checklist' && checklist.length > 0 ? `(${checklistProgress}%)` : ''}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ ...cardSt, borderTopLeftRadius: 0 }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>📋 {L.overview}</h3>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.type}</span><span style={infoValueSt}>{wo.wo_type}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.source}</span><span style={infoValueSt}>{wo.source}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.scheduled}</span><span style={infoValueSt}>{formatDate(wo.scheduled_date)} {wo.scheduled_time_start || ''}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.assigned_to}</span><span style={infoValueSt}>{wo.assigned_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.actual_start}</span><span style={infoValueSt}>{formatDateTime(wo.actual_start)}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.actual_end}</span><span style={infoValueSt}>{formatDateTime(wo.actual_end)}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.created}</span><span style={infoValueSt}>{formatDateTime(wo.created_at)}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.created_by}</span><span style={infoValueSt}>{wo.created_by_name || '-'}</span></div>
              {wo.description && <div style={{ marginTop: '12px' }}><span style={infoLabelSt}>{L.description}</span><p style={{ color: '#d1d5db', fontSize: '13px', marginTop: '4px' }}>{isAr && wo.description_ar ? wo.description_ar : wo.description}</p></div>}
              {wo.resolution_summary && <div style={{ marginTop: '12px' }}><span style={infoLabelSt}>{L.resolution}</span><p style={{ color: '#10b981', fontSize: '13px', marginTop: '4px' }}>{wo.resolution_summary}</p></div>}
            </div>
            <div>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>🏗️ {L.equipment} & {L.contract}</h3>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.equipment}</span><span style={infoValueSt}>{isAr && wo.equipment_name_ar ? wo.equipment_name_ar : wo.equipment_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.client}</span><span style={infoValueSt}>{isAr && wo.client_name_ar ? wo.client_name_ar : wo.client_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.location}</span><span style={infoValueSt}>{isAr && wo.location_name_ar ? wo.location_name_ar : wo.location_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.contract}</span><span style={infoValueSt}>{wo.contract_number || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.coverage}</span><span style={infoValueSt}>{wo.coverage_type || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.billable}</span><span style={{ ...infoValueSt, color: wo.is_billable ? '#ef4444' : '#10b981' }}>{wo.is_billable ? '💰 Yes' : '✅ No'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.sla_response}</span><span style={infoValueSt}>{wo.sla_response_hours ? `${wo.sla_response_hours}h` : '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.sla_resolution}</span><span style={infoValueSt}>{wo.sla_resolution_hours ? `${wo.sla_resolution_hours}h` : '-'}</span></div>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', margin: '20px 0 12px' }}>💰 {L.total_cost}</h3>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.labor_cost}</span><span style={infoValueSt}>{Number(wo.total_labor_cost).toFixed(2)}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.parts_cost}</span><span style={infoValueSt}>{Number(wo.total_parts_cost).toFixed(2)}</span></div>
              <div style={{ ...infoRowSt, borderBottom: 'none' }}><span style={{ ...infoLabelSt, fontWeight: '700', color: '#f9fafb' }}>{L.total_cost}</span><span style={{ ...infoValueSt, fontSize: '16px', color: '#667eea' }}>{Number(wo.total_cost).toFixed(2)}</span></div>
            </div>
          </div>
        )}

        {/* CHECKLIST TAB */}
        {activeTab === 'checklist' && (
          <div>
            {checklist.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>{checklistProgress}% complete</span>
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>{checklist.filter(c => c.checked).length}/{checklist.length}</span>
                </div>
                <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px' }}>
                  <div style={{ width: `${checklistProgress}%`, height: '100%', borderRadius: '3px', backgroundColor: '#667eea', transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            {checklist.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div>
            ) : checklist.map((item, idx) => (
              <div key={idx} onClick={() => handleChecklistToggle(idx)} style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', cursor: 'pointer',
                borderBottom: '1px solid rgba(255,255,255,0.06)', transition: 'background 0.15s',
              }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                 onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div style={{
                  width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: item.checked ? 'none' : '2px solid rgba(255,255,255,0.3)',
                  backgroundColor: item.checked ? '#667eea' : 'transparent', color: '#fff', fontSize: '13px',
                }}>
                  {item.checked && '✓'}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ color: item.checked ? '#6b7280' : '#f9fafb', fontSize: '13px', textDecoration: item.checked ? 'line-through' : 'none' }}>{item.item}</span>
                  {item.item_ar && <div style={{ color: '#6b7280', fontSize: '12px', direction: 'rtl' }}>{item.item_ar}</div>}
                </div>
                {item.checked_at && <span style={{ color: '#6b7280', fontSize: '11px' }}>{formatDateTime(item.checked_at)}</span>}
              </div>
            ))}
          </div>
        )}

        {/* TIME & PARTS TAB */}
        {activeTab === 'time_parts' && (
          <div>
            {/* Time Logs */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', margin: 0 }}>⏱️ {lang === 'ar' ? 'سجل الوقت' : 'Time Logs'}</h3>
              {!['closed', 'cancelled'].includes(wo.status) && <button style={btnPrimary} onClick={() => setShowTimeLog(true)}>+ {L.add_time}</button>}
            </div>
            {(wo.time_logs || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', marginBottom: '20px' }}>{L.no_items}</div>
            ) : (wo.time_logs || []).map(tl => (
              <div key={tl.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <span style={badgeSt('#8b5cf6')}>{tl.time_type}</span>
                  <span style={{ color: '#d1d5db', fontSize: '13px', marginLeft: '10px' }}>{tl.agent_name} — {tl.duration_minutes}min</span>
                  {tl.notes && <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>{tl.notes}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#f59e0b', fontSize: '13px', fontWeight: '600' }}>{tl.cost ? `${Number(tl.cost).toFixed(2)}` : '-'}</span>
                  {!['closed', 'cancelled'].includes(wo.status) && <button style={{ ...btnDanger, padding: '4px 8px', fontSize: '11px' }} onClick={() => handleDeleteTimeLog(tl.id)}>✕</button>}
                </div>
              </div>
            ))}

            {/* Parts */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 12px' }}>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', margin: 0 }}>🔩 {lang === 'ar' ? 'القطع المستخدمة' : 'Parts Used'}</h3>
              {!['closed', 'cancelled'].includes(wo.status) && <button style={btnPrimary} onClick={() => setShowPartForm(true)}>+ {L.add_part}</button>}
            </div>
            {(wo.parts_used || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>{L.no_items}</div>
            ) : (wo.parts_used || []).map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <span style={{ color: '#f9fafb', fontSize: '13px', fontWeight: '500' }}>{p.part_name}</span>
                  {p.part_number && <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>#{p.part_number}</span>}
                  <div style={{ color: '#9ca3af', fontSize: '12px' }}>Qty: {p.quantity} × {Number(p.unit_cost).toFixed(2)} {p.markup_percent > 0 ? `(+${p.markup_percent}%)` : ''}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#f59e0b', fontSize: '13px', fontWeight: '600' }}>{Number(p.total_cost).toFixed(2)}</span>
                  {!['closed', 'cancelled'].includes(wo.status) && <button style={{ ...btnDanger, padding: '4px 8px', fontSize: '11px' }} onClick={() => handleDeletePart(p.id)}>✕</button>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ATTACHMENTS TAB */}
        {activeTab === 'invoices' && (
          <div>
            {/* Invoice Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <div style={{ backgroundColor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
                <div style={{ color: '#93c5fd', fontSize: '12px', marginBottom: '4px' }}>{isAr ? 'إجمالي أمر العمل' : 'WO Total'}</div>
                <div style={{ color: '#f9fafb', fontSize: '20px', fontWeight: 600 }}>{invoiceSummary.wo_total?.toFixed(2) || '0.00'}</div>
              </div>
              <div style={{ backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
                <div style={{ color: '#6ee7b7', fontSize: '12px', marginBottom: '4px' }}>{L.total_invoiced}</div>
                <div style={{ color: '#f9fafb', fontSize: '20px', fontWeight: 600 }}>{invoiceSummary.total_invoiced?.toFixed(2) || '0.00'}</div>
              </div>
              <div style={{ backgroundColor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
                <div style={{ color: '#fcd34d', fontSize: '12px', marginBottom: '4px' }}>{L.remaining_amount}</div>
                <div style={{ color: '#f9fafb', fontSize: '20px', fontWeight: 600 }}>{invoiceSummary.remaining?.toFixed(2) || '0.00'}</div>
              </div>
            </div>

            {/* Generate Button */}
            {invoiceSummary.can_invoice && (
              <div style={{ marginBottom: '20px' }}>
                <button onClick={handleGenerateInvoice} disabled={invoiceGenerating}
                  style={{ padding: '10px 24px', backgroundColor: invoiceGenerating ? '#374151' : '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: invoiceGenerating ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: invoiceGenerating ? 0.6 : 1 }}>
                  {invoiceGenerating ? '...' : L.create_invoice}
                </button>
              </div>
            )}

            {/* Invoice List */}
            {invoiceLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
            ) : invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {invoices.map((inv) => (
                  <div key={inv.id} style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '14px', cursor: 'pointer', transition: 'border-color 0.2s' }}
                    onClick={() => fetchInvoiceDetail(inv.id)}
                    onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)'}
                    onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <span style={{ color: '#f9fafb', fontWeight: 600, fontSize: '14px' }}>{L.invoice_no}{inv.id}</span>
                        {inv.erp_invoice_id && <span style={{ color: '#6ee7b7', fontSize: '12px', marginLeft: '8px' }}>ERP: {inv.erp_invoice_id}</span>}
                      </div>
                      <span style={{ color: '#10b981', fontWeight: 700, fontSize: '16px' }}>{parseFloat(inv.net_total || inv.invoice_total).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>{L.parts_lbl}: {parseFloat(inv.parts_total).toFixed(2)}</span>
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>{L.labor_lbl}: {parseFloat(inv.labor_total).toFixed(2)}</span>
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>{isAr ? 'بنود' : 'Items'}: {inv.items_count}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#6b7280', fontSize: '11px' }}>{inv.created_by_name || 'System'}</span>
                      <span style={{ color: '#6b7280', fontSize: '11px' }}>{formatDateDMY(inv.invoice_date || inv.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Invoice Detail Modal */}
            {selectedInvoice && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                onClick={() => { setSelectedInvoice(null); setInvoiceDetail(null); setEditingInvoice(false); }}>
                <div style={{ backgroundColor: '#1e293b', borderRadius: '12px', padding: '24px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)' }}
                  onClick={e => e.stopPropagation()}>
                  {invoiceDetailLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
                  ) : invoiceDetail ? (
                    <div>
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                        <h3 style={{ color: '#f9fafb', fontSize: '18px', margin: 0 }}>{L.invoice_no}{invoiceDetail.id}</h3>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {!invoiceDetail.erp_invoice_id && !editingInvoice && (
                            <>
                              <button onClick={() => setEditingInvoice(true)}
                                style={{ padding: '6px 14px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                {L.edit_invoice}
                              </button>
                              <button onClick={() => handleDeleteInvoice(invoiceDetail.id)}
                                style={{ padding: '6px 14px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                {L.delete_invoice}
                              </button>
                            </>
                          )}
                          {editingInvoice && (
                            <>
                              <button onClick={handleSaveInvoiceEdit}
                                style={{ padding: '6px 14px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                {L.save_changes}
                              </button>
                              <button onClick={() => { setEditingInvoice(false); setEditItems((invoiceDetail.items || []).map(it => ({ ...it }))); setEditNotes(invoiceDetail.notes || ''); setEditDiscount(parseFloat(invoiceDetail.discount_percent) || 0); setEditTax(parseFloat(invoiceDetail.tax_percent) || 0); setEditDate(invoiceDetail.invoice_date ? invoiceDetail.invoice_date.slice(0,10) : ''); }}
                                style={{ padding: '6px 14px', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                {isAr ? 'إلغاء' : 'Cancel'}
                              </button>
                            </>
                          )}
                          <button onClick={() => { setSelectedInvoice(null); setInvoiceDetail(null); setEditingInvoice(false); }}
                            style={{ padding: '6px 14px', backgroundColor: '#374151', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                            {L.close_lbl}
                          </button>
                        </div>
                      </div>

                      {/* Info */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px', fontSize: '13px' }}>
                        <div><span style={{ color: '#6b7280' }}>{isAr ? 'رقم أمر العمل' : 'WO'}:</span> <span style={{ color: '#f9fafb' }}>{invoiceDetail.wo_number}</span></div>
                        <div><span style={{ color: '#6b7280' }}>{isAr ? 'العميل' : 'Client'}:</span> <span style={{ color: '#f9fafb' }}>{invoiceDetail.client_name}</span></div>
                        <div>
                          <span style={{ color: '#6b7280' }}>{isAr ? 'التاريخ' : 'Date'}:</span>{' '}
                          {editingInvoice ? (
                            <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
                              style={{ backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '4px 8px', fontSize: '12px' }} />
                          ) : <span style={{ color: '#f9fafb' }}>{formatDateDMY(invoiceDetail.invoice_date || invoiceDetail.created_at)}</span>}
                        </div>
                        <div><span style={{ color: '#6b7280' }}>{isAr ? 'بواسطة' : 'By'}:</span> <span style={{ color: '#f9fafb' }}>{invoiceDetail.created_by_name || 'System'}</span></div>
                      </div>

                      {/* Summary row */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(16,185,129,0.1)', borderRadius: '8px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>{isAr ? 'المجموع' : 'Subtotal'}</div>
                          <div style={{ color: '#f9fafb', fontWeight: 600 }}>{parseFloat(invoiceDetail.invoice_total).toFixed(2)}</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>{isAr ? 'خصم' : 'Discount'}</div>
                          {editingInvoice ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                              <input type="number" step="0.01" value={editDiscount} onChange={e => setEditDiscount(e.target.value)}
                                style={{ width: '60px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '3px', textAlign: 'center', fontSize: '12px' }} />
                              <span style={{ color: '#9ca3af', fontSize: '12px' }}>%</span>
                            </div>
                          ) : <div style={{ color: '#ef4444', fontWeight: 600 }}>{parseFloat(invoiceDetail.discount_percent || 0).toFixed(1)}% ({parseFloat(invoiceDetail.discount_value || 0).toFixed(2)})</div>}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>{isAr ? 'ضريبة' : 'Tax'}</div>
                          {editingInvoice ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                              <input type="number" step="0.01" value={editTax} onChange={e => setEditTax(e.target.value)}
                                style={{ width: '60px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '3px', textAlign: 'center', fontSize: '12px' }} />
                              <span style={{ color: '#9ca3af', fontSize: '12px' }}>%</span>
                            </div>
                          ) : <div style={{ color: '#fcd34d', fontWeight: 600 }}>{parseFloat(invoiceDetail.tax_percent || 0).toFixed(1)}% ({parseFloat(invoiceDetail.tax_amount || 0).toFixed(2)})</div>}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>{isAr ? 'الصافي' : 'Net Total'}</div>
                          <div style={{ color: '#10b981', fontWeight: 700, fontSize: '16px' }}>{parseFloat(invoiceDetail.net_total || invoiceDetail.invoice_total).toFixed(2)}</div>
                        </div>
                      </div>

                      {/* Notes */}
                      {editingInvoice ? (
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{L.notes_lbl}</label>
                          <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2}
                            style={{ width: '100%', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '8px', fontSize: '13px', resize: 'vertical' }} />
                        </div>
                      ) : invoiceDetail.notes ? (
                        <div style={{ marginBottom: '16px', color: '#9ca3af', fontSize: '13px' }}>{invoiceDetail.notes}</div>
                      ) : null}

                      {/* Items Table */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <th style={{ padding: '8px', color: '#9ca3af', textAlign: isAr ? 'right' : 'left' }}>{L.type_lbl}</th>
                              <th style={{ padding: '8px', color: '#9ca3af', textAlign: isAr ? 'right' : 'left' }}>{L.item_name}</th>
                              <th style={{ padding: '8px', color: '#9ca3af', textAlign: 'center' }}>{L.qty_lbl}</th>
                              <th style={{ padding: '8px', color: '#9ca3af', textAlign: 'center' }}>{L.price_lbl}</th>
                              <th style={{ padding: '8px', color: '#9ca3af', textAlign: 'center' }}>{L.markup_lbl}</th>
                              <th style={{ padding: '8px', color: '#9ca3af', textAlign: 'center' }}>{L.total_lbl}</th>
                              {editingInvoice && <th style={{ padding: '8px', width: '40px' }}></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {(editingInvoice ? editItems : (invoiceDetail.items || [])).map((item, idx) => (
                              <tr key={item.id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <td style={{ padding: '8px' }}>
                                  <span style={{ backgroundColor: item.item_type === 'part' ? 'rgba(59,130,246,0.2)' : 'rgba(245,158,11,0.2)', color: item.item_type === 'part' ? '#93c5fd' : '#fcd34d', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                                    {item.item_type === 'part' ? L.parts_lbl : L.labor_lbl}
                                  </span>
                                </td>
                                <td style={{ padding: '8px', color: '#f9fafb' }}>{item.name}{item.part_number ? ` (${item.part_number})` : ''}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  {editingInvoice ? (
                                    <input type="number" step="0.01" value={item.qty} onChange={e => updateEditItem(idx, 'qty', e.target.value)}
                                      style={{ width: '70px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '4px', textAlign: 'center', fontSize: '12px' }} />
                                  ) : <span style={{ color: '#f9fafb' }}>{parseFloat(item.qty).toFixed(2)}</span>}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  {editingInvoice ? (
                                    <input type="number" step="0.01" value={item.unit_price} onChange={e => updateEditItem(idx, 'unit_price', e.target.value)}
                                      style={{ width: '80px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '4px', textAlign: 'center', fontSize: '12px' }} />
                                  ) : <span style={{ color: '#f9fafb' }}>{parseFloat(item.unit_price).toFixed(2)}</span>}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  {editingInvoice && item.item_type === 'part' ? (
                                    <input type="number" step="0.01" value={item.markup_percent} onChange={e => updateEditItem(idx, 'markup_percent', e.target.value)}
                                      style={{ width: '60px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '4px', textAlign: 'center', fontSize: '12px' }} />
                                  ) : <span style={{ color: '#9ca3af' }}>{parseFloat(item.markup_percent || 0).toFixed(0)}%</span>}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', color: '#10b981', fontWeight: 600 }}>{parseFloat(item.total).toFixed(2)}</td>
                                {editingInvoice && <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <button onClick={() => handleRemoveItem(item.id)}
                                    style={{ backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '2px' }}>×</button>
                                </td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Add Item */}
                      {editingInvoice && (
                        <div style={{ marginTop: '12px' }}>
                          {!showAddItem ? (
                            <button onClick={() => setShowAddItem(true)}
                              style={{ padding: '6px 16px', backgroundColor: 'rgba(59,130,246,0.2)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                              + {isAr ? 'إضافة بند' : 'Add Item'}
                            </button>
                          ) : (
                            <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <div>
                                <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block' }}>{L.type_lbl}</label>
                                <select value={newItem.item_type} onChange={e => setNewItem(p => ({ ...p, item_type: e.target.value }))}
                                  style={{ backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '5px', fontSize: '12px' }}>
                                  <option value="part">{L.parts_lbl}</option>
                                  <option value="labor">{L.labor_lbl}</option>
                                </select>
                              </div>
                              <div style={{ flex: 1, minWidth: '120px' }}>
                                <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block' }}>{L.item_name}</label>
                                <input value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))}
                                  style={{ width: '100%', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '5px', fontSize: '12px' }} />
                              </div>
                              <div>
                                <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block' }}>{L.qty_lbl}</label>
                                <input type="number" step="0.01" value={newItem.qty} onChange={e => setNewItem(p => ({ ...p, qty: e.target.value }))}
                                  style={{ width: '60px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '5px', textAlign: 'center', fontSize: '12px' }} />
                              </div>
                              <div>
                                <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block' }}>{L.price_lbl}</label>
                                <input type="number" step="0.01" value={newItem.unit_price} onChange={e => setNewItem(p => ({ ...p, unit_price: e.target.value }))}
                                  style={{ width: '80px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '5px', textAlign: 'center', fontSize: '12px' }} />
                              </div>
                              <div>
                                <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block' }}>{L.markup_lbl}</label>
                                <input type="number" step="0.01" value={newItem.markup_percent} onChange={e => setNewItem(p => ({ ...p, markup_percent: e.target.value }))}
                                  style={{ width: '60px', backgroundColor: '#0f172a', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '5px', textAlign: 'center', fontSize: '12px' }} />
                              </div>
                              <button onClick={handleAddItem}
                                style={{ padding: '5px 14px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                {isAr ? 'إضافة' : 'Add'}
                              </button>
                              <button onClick={() => setShowAddItem(false)}
                                style={{ padding: '5px 14px', backgroundColor: '#374151', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                {isAr ? 'إلغاء' : 'Cancel'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Edit mode total preview */}
                      {editingInvoice && (
                        <div style={{ marginTop: '12px', padding: '10px', backgroundColor: 'rgba(16,185,129,0.1)', borderRadius: '6px', textAlign: 'center' }}>
                          <span style={{ color: '#6ee7b7', fontWeight: 600, fontSize: '15px' }}>
                            {(() => { const sub = editItems.reduce((s, it) => s + (parseFloat(it.total) || 0), 0); const dsc = sub * (parseFloat(editDiscount) || 0) / 100; const aft = sub - dsc; const tx = aft * (parseFloat(editTax) || 0) / 100; return (isAr ? 'الصافي الجديد' : 'New Net') + ': ' + (aft + tx).toFixed(2) + ' (' + (isAr ? 'مجموع' : 'Sub') + ': ' + sub.toFixed(2) + ' - ' + (isAr ? 'خصم' : 'Disc') + ': ' + dsc.toFixed(2) + ' + ' + (isAr ? 'ضريبة' : 'Tax') + ': ' + tx.toFixed(2) + ')'; })()}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'attachments' && (
          <div>
            {!['closed', 'cancelled'].includes(wo.status) && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  📎 {L.upload}
                  <input type="file" style={{ display: 'none' }} onChange={handleUploadFile} />
                </label>
              </div>
            )}
            {(wo.attachments || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                {(wo.attachments || []).map(att => (
                  <div key={att.id} style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {att.mime_type && att.mime_type.startsWith('image/') ? (
                      <img src={`/${att.file_path}`} alt={att.original_filename} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', marginBottom: '8px' }} />
                    ) : (
                      <div style={{ width: '100%', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', marginBottom: '8px' }}>📄</div>
                    )}
                    <div style={{ color: '#d1d5db', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_filename}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                      <span style={badgeSt('#6b7280')}>{att.attachment_type}</span>
                      <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }} onClick={() => handleDeleteAttachment(att.id)}>✕ {L.delete}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div>
            {(wo.status_history || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div>
            ) : (wo.status_history || []).map((h, idx) => (
              <div key={h.id || idx} style={{ display: 'flex', gap: '14px', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: STATUS_COLORS[h.to_status] || '#6b7280' }} />
                  {idx < (wo.status_history || []).length - 1 && <div style={{ width: '2px', flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', marginTop: '4px' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {h.from_status && <span style={badgeSt('#6b7280')}>{h.from_status}</span>}
                    {h.from_status && <span style={{ color: '#6b7280' }}>→</span>}
                    <span style={badgeSt(STATUS_COLORS[h.to_status] || '#6b7280')}>{h.to_status}</span>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>by {h.changed_by_name || 'System'}</span>
                  </div>
                  {h.notes && <p style={{ color: '#9ca3af', fontSize: '12px', margin: '4px 0 0' }}>{h.notes}</p>}
                  <span style={{ color: '#6b7280', fontSize: '11px' }}>{formatDateTime(h.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODALS */}
      {/* Status Change Modal */}
      {showStatusModal && (
        <div style={modalOverlay} onClick={() => setShowStatusModal(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#f9fafb', fontSize: '16px', marginBottom: '16px' }}>
              {L.change_status}: <span style={badgeSt(STATUS_COLORS[targetStatus])}>{targetStatus}</span>
            </h3>
            {targetStatus === 'completed' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelSt}>{L.resolution} *</label>
                <textarea style={{ ...inputSt, minHeight: '80px' }} value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)} />
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelSt}>{L.notes}</label>
              <input type="text" style={inputSt} value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={btnOutline} onClick={() => setShowStatusModal(false)}>{L.cancel}</button>
              <button style={btnPrimary} onClick={handleStatusChange}>{L.confirm}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssign && (
        <div style={modalOverlay} onClick={() => setShowAssign(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#f9fafb', fontSize: '16px', marginBottom: '16px' }}>👤 {L.assign}</h3>
            <label style={labelSt}>{L.select_tech}</label>
            <select style={inputSt} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
              <option value="">--</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={btnOutline} onClick={() => setShowAssign(false)}>{L.cancel}</button>
              <button style={btnPrimary} onClick={handleAssign}>{L.confirm}</button>
            </div>
          </div>
        </div>
      )}

      {/* Time Log Modal */}
      {showTimeLog && (
        <div style={modalOverlay} onClick={() => setShowTimeLog(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#f9fafb', fontSize: '16px', marginBottom: '16px' }}>⏱️ {L.add_time}</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={labelSt}>{L.time_type}</label>
                <select style={inputSt} value={timeLogData.time_type} onChange={(e) => setTimeLogData(p => ({ ...p, time_type: e.target.value }))}>
                  <option value="work">{isAr ? 'عمل' : 'Work'}</option>
                  <option value="travel">{isAr ? 'سفر' : 'Travel'}</option>
                  <option value="overtime">{isAr ? 'إضافي' : 'Overtime'}</option>
                  <option value="emergency">{isAr ? 'طوارئ' : 'Emergency'}</option>
                </select>
              </div>
              <div>
                <label style={labelSt}>{L.start} *</label>
                <input type="datetime-local" style={inputSt} value={timeLogData.start_time} onChange={(e) => setTimeLogData(p => ({ ...p, start_time: e.target.value }))} />
              </div>
              <div>
                <label style={labelSt}>{L.end}</label>
                <input type="datetime-local" style={inputSt} value={timeLogData.end_time} onChange={(e) => setTimeLogData(p => ({ ...p, end_time: e.target.value }))} />
              </div>
              <div>
                <label style={labelSt}>{L.notes}</label>
                <input type="text" style={inputSt} value={timeLogData.notes} onChange={(e) => setTimeLogData(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={btnOutline} onClick={() => setShowTimeLog(false)}>{L.cancel}</button>
              <button style={btnPrimary} onClick={handleAddTimeLog}>{L.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Part Form Modal */}
      {showPartForm && (
        <div style={modalOverlay} onClick={() => setShowPartForm(false)}>
          <div style={{...modalBox, maxWidth: '500px'}} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#f9fafb', fontSize: '16px', marginBottom: '16px' }}>🔩 {L.add_part}</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ position: 'relative' }}>
                <label style={labelSt}>{isAr ? 'بحث منتج ERP' : 'Search ERP Product'}</label>
                <input type="text" style={inputSt} value={erpSearch}
                  placeholder={isAr ? 'اكتب للبحث في منتجات ERP...' : 'Type to search ERP products...'}
                  onChange={(e) => { setErpSearch(e.target.value); searchErpProducts(e.target.value); }}
                  onFocus={() => { if (erpResults.length > 0) setShowErpDropdown(true); }}
                />
                {erpSearching && <div style={{ position: 'absolute', right: '12px', top: '32px', color: '#9ca3af', fontSize: '11px' }}>...</div>}
                {showErpDropdown && erpResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: '200px', overflowY: 'auto', backgroundColor: '#1a2038', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', marginTop: '4px' }}>
                    {erpResults.map(p => (
                      <div key={p.id} onClick={() => selectErpProduct(p)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '13px' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,123,255,0.15)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <div style={{ fontWeight: '600', color: '#f9fafb' }}>{p.name}</div>
                        <div style={{ display: 'flex', gap: '12px', color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>
                          <span>{isAr ? 'كود' : 'Code'}: {p.code}</span>
                          {p.cost_price != null && <span>{isAr ? 'تكلفة' : 'Cost'}: {p.cost_price}</span>}
                          {p.total_qty != null && <span>{isAr ? 'مخزون' : 'Stock'}: {p.total_qty}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
                <label style={labelSt}>{L.part_name} *</label>
                <input type="text" style={inputSt} value={partData.part_name} onChange={(e) => setPartData(p => ({ ...p, part_name: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={labelSt}>{isAr ? 'رقم القطعة' : 'Part #'}</label>
                  <input type="text" style={inputSt} value={partData.part_number || ''} onChange={(e) => setPartData(p => ({ ...p, part_number: e.target.value }))} />
                </div>
                <div>
                  <label style={labelSt}>{isAr ? 'الاسم بالعربي' : 'Name (AR)'}</label>
                  <input type="text" style={{...inputSt, direction: 'rtl'}} value={partData.part_name_ar || ''} onChange={(e) => setPartData(p => ({ ...p, part_name_ar: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={labelSt}>{L.quantity}</label>
                  <input type="number" style={inputSt} value={partData.quantity} onChange={(e) => setPartData(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label style={labelSt}>{L.unit_cost}</label>
                  <input type="number" step="0.01" style={inputSt} value={partData.unit_cost} onChange={(e) => setPartData(p => ({ ...p, unit_cost: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
              {partData.erp_item_id && (
                <div style={{ fontSize: '11px', color: '#4ade80', padding: '4px 8px', backgroundColor: 'rgba(74,222,128,0.1)', borderRadius: '6px' }}>
                  ✅ {isAr ? 'مرتبط بمنتج ERP #' : 'Linked to ERP product #'}{partData.erp_item_id}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={btnOutline} onClick={() => { setShowPartForm(false); setErpSearch(''); setErpResults([]); setShowErpDropdown(false); }}>{L.cancel}</button>
              <button style={btnPrimary} onClick={handleAddPart}>{L.save}</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default WorkOrderDetail;
